import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { actionErrorMessage, isPermissionDenied } from "../../../utils/network";
import { SettingsAPI, VisitNoteEditPolicySettings } from "../../../services/settings.service";
import { cacheHrfSettings } from "../../../hooks/useHrfSettings";
import { cacheVisitNoteEditPolicy } from "../../../hooks/useVisitNoteEditPolicy";
import { cacheNoteEditPolicy } from "../../../hooks/useNoteEditPolicy";
import { NotesAPI, NoteEditPolicy } from "../../../services/notes.service";
import { cacheAppointmentConfig } from "../../../hooks/useAppointmentConfig";
import { SettingsCard } from "../SettingsCard";
import { EditableCardFooter } from "../EditableCardFooter";
import { Toggle } from "../../../components/ui/Toggle";
import { ImageActionsModal } from "./ImageActionsModal";
import { ClinicClosureCard } from "./ClinicClosureCard";
import { useClinic } from "../../../contexts/ClinicContext";
import { OrganizationAPI } from "../../../services/organization.service";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";

// ── Address helpers ──────────────────────────────────────────────────────────
// The backend only stores one free-text `address` column. The Settings card
// splits/joins it into Street / City / Pincode / State — the same shape and
// join order onboarding (ClinicDetailsPage) uses — so both flows stay in sync.
//
// The split must be lossless: this address prints on the prescription PDF.
// City / Pincode / State are taken from the right so a street containing
// commas stays whole, and an address that doesn't have all four parts goes
// into Street untouched rather than being spread across the wrong fields.

const splitAddress = (address: string) => {
    const parts = address.split(",").map(s => s.trim()).filter(Boolean);
    if (parts.length < 4) {
        return { streetAddress: parts.join(", "), city: "", pincode: "", state: "" };
    }
    const [city, pincode, state] = parts.slice(-3);
    return { streetAddress: parts.slice(0, -3).join(", "), city, pincode, state };
};

const joinAddress = (a: { streetAddress: string; city: string; pincode: string; state: string }) =>
    [a.streetAddress, a.city, a.pincode, a.state].filter(Boolean).join(", ");

const validateWebsite = (value: string): string | undefined => {
    if (!value.trim()) return undefined;   // optional field
    const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/.*)?$/;
    return urlPattern.test(value) ? undefined : "Please enter a valid website URL";
};

// ── Operating Hours / Days Open helpers ──────────────────────────────────────
// No `days_open` column exists on the backend, so both pieces are folded into
// the existing free-text `timings_display` column (e.g. "Mon - Fri • 9:00 AM -
// 6:00 PM") and parsed back apart on load. Replacing the old raw-text inputs
// with pickers means a bogus string can no longer be typed into either field.

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatDaysDisplay = (days: number[]): string => {
    if (days.length === 0) return "";
    const sorted = [...days].sort((a, b) => a - b);
    const isContiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
    if (isContiguous && sorted.length > 1) {
        return `${DAY_LABELS[sorted[0]]} - ${DAY_LABELS[sorted[sorted.length - 1]]}`;
    }
    return sorted.map(d => DAY_LABELS[d]).join(", ");
};

const parseDaysDisplay = (text: string): number[] => {
    const trimmed = text.trim();
    if (!trimmed) return [];
    const rangeMatch = trimmed.match(/^(\w{3})\s*-\s*(\w{3})$/);
    if (rangeMatch) {
        const start = DAY_LABELS.indexOf(rangeMatch[1]);
        const end = DAY_LABELS.indexOf(rangeMatch[2]);
        if (start !== -1 && end !== -1 && start <= end) {
            return Array.from({ length: end - start + 1 }, (_, i) => start + i);
        }
    }
    return trimmed.split(",").map(s => DAY_LABELS.indexOf(s.trim())).filter(d => d !== -1);
};

// "HH:MM" (24h, from <input type="time">) ↔ "H:MM AM/PM" (display)
const to12h = (time24: string): string => {
    const [h, m] = time24.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

const to24h = (time12: string): string | null => {
    const match = time12.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    let h = parseInt(match[1], 10);
    const period = match[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${match[2]}`;
};

const formatHoursDisplay = (opensAt: string, closesAt: string): string =>
    opensAt && closesAt ? `${to12h(opensAt)} - ${to12h(closesAt)}` : "";

const parseHoursDisplay = (text: string): { opensAt: string; closesAt: string } => {
    const match = text.trim().match(/^(.+?)\s*-\s*(.+)$/);
    if (!match) return { opensAt: "", closesAt: "" };
    return { opensAt: to24h(match[1]) ?? "", closesAt: to24h(match[2]) ?? "" };
};

const buildTimingsDisplay = (daysOpen: number[], opensAt: string, closesAt: string): string =>
    [formatDaysDisplay(daysOpen), formatHoursDisplay(opensAt, closesAt)].filter(Boolean).join(" • ");

const parseTimingsDisplay = (text: string): { daysOpen: number[]; opensAt: string; closesAt: string } => {
    const result = { daysOpen: [] as number[], opensAt: "", closesAt: "" };
    for (const part of text.split("•").map(s => s.trim()).filter(Boolean)) {
        if (/\d{1,2}:\d{2}\s*(AM|PM)/i.test(part)) {
            const hours = parseHoursDisplay(part);
            result.opensAt = hours.opensAt;
            result.closesAt = hours.closesAt;
        } else {
            result.daysOpen = parseDaysDisplay(part);
        }
    }
    return result;
};

// ── Main component ────────────────────────────────────────────────────────────

export const ClinicSettings: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const { isFeatureAccessible } = useFeatureFlags();
    const immunizationEnabled = isFeatureAccessible("immunization");

    // ── Organisation ──────────────────────────────────────────────────────────
    // Owner-only. The org name is never asked for at creation time — it is
    // derived from the first clinic with its locality stripped — so every
    // organisation starts life with a guess. This card is the correction.
    const { isOrgOwner, organizationName, refresh: refreshOrg } = useClinic();
    const [orgName, setOrgName] = useState("");
    const [savingOrg, setSavingOrg] = useState(false);
    const [editingOrg, setEditingOrg] = useState(false);
    const orgSnapshotRef = useRef("");

    useEffect(() => {
        // Only sync from context while the card is locked; doing it mid-edit
        // would overwrite what the user is typing on any context refresh.
        if (!editingOrg) setOrgName(organizationName ?? "");
    }, [organizationName, editingOrg]);

    const handleEditOrg = () => {
        orgSnapshotRef.current = orgName;
        setEditingOrg(true);
    };
    const handleCancelEditOrg = () => {
        setOrgName(orgSnapshotRef.current);
        setEditingOrg(false);
    };
    const handleSaveOrg = async () => {
        const trimmed = orgName.trim();
        if (!trimmed) {
            toast.error("Organisation name cannot be empty.");
            return;
        }
        if (trimmed === organizationName) {
            setEditingOrg(false);
            return;
        }
        setSavingOrg(true);
        try {
            await OrganizationAPI.rename(trimmed);
            // Re-resolve so the switcher header and the clinic picker pick up
            // the new name without a reload.
            await refreshOrg();
            setEditingOrg(false);
            toast.success("Organisation name updated.");
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : "Could not rename the organisation.",
            );
        } finally {
            setSavingOrg(false);
        }
    };

    // ── Clinic Profile form ───────────────────────────────────────────────────
    const [clinicForm, setClinicForm] = useState({
        name:          "",
        streetAddress: "",
        city:          "",
        pincode:       "",
        state:         "",
        phone:         "",
        email:         "",
        website:       "",
        opensAt:       "",   // "HH:MM" 24h, from <input type="time">
        closesAt:      "",   // "HH:MM" 24h
        daysOpen:      [] as number[],   // 0=Mon … 6=Sun
    });
    const [savingClinic, setSavingClinic] = useState(false);
    const [websiteError, setWebsiteError] = useState<string | undefined>();
    // Saved timings the pickers can't represent (e.g. "9am-1pm, 5pm-9pm").
    // Shown as a hint and left untouched unless the user edits hours or days.
    const [legacyTimings, setLegacyTimings] = useState<string | null>(null);
    // Each card stays locked until the user clicks Edit. The snapshot captures
    // the form when editing starts so Cancel can restore it. Guards against
    // accidentally saving changes the user never meant to make.
    const [editingClinic, setEditingClinic] = useState(false);
    const clinicSnapshotRef = useRef(clinicForm);

    // ── Clinic logo ───────────────────────────────────────────────────────────
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const logoInputRef = useRef<HTMLInputElement>(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [deletingLogo, setDeletingLogo] = useState(false);
    const [showLogoModal, setShowLogoModal] = useState(false);

    // ── Prescription letterhead ───────────────────────────────────────────────
    const [letterheadUrl, setLetterheadUrl] = useState<string | null>(null);
    const letterheadInputRef = useRef<HTMLInputElement>(null);
    const [uploadingLetterhead, setUploadingLetterhead] = useState(false);
    const [deletingLetterhead, setDeletingLetterhead] = useState(false);
    const [showLetterheadModal, setShowLetterheadModal] = useState(false);


    // ── Appointment config ────────────────────────────────────────────────────
    const [apptForm, setApptForm] = useState({
        slot_duration_minutes: 15,
        max_daily_bookings:    50,
        allow_online_booking:  true,
    });
    const [savingAppt, setSavingAppt] = useState(false);
    const [editingAppt, setEditingAppt] = useState(false);
    const apptSnapshotRef = useRef(apptForm);
    // Last persisted slot duration — lets the save detect a duration change,
    // which propagates to every doctor's roster and rebuilds upcoming slots.
    const savedSlotDurationRef = useRef<number>(15);

    // ── HRF & MPR Settings ────────────────────────────────────────────────────
    const [hrfForm, setHrfForm] = useState({
        mandatory_vitals:    true,
        mandatory_diagnosis: true,
        lab_report_uploads:  false,
    });
    const [savingHrf, setSavingHrf] = useState(false);
    const [editingHrf, setEditingHrf] = useState(false);
    const hrfSnapshotRef = useRef(hrfForm);

    // ── Reschedule policy ─────────────────────────────────────────────────────
    const [reschedForm, setReschedForm] = useState({
        self_reschedule_cutoff_hours:     24,
        max_self_reschedules_per_patient: 2,
        excessive_churn_threshold:        3,
        excessive_churn_window_days:      90,
    });
    const [savingResched, setSavingResched] = useState(false);
    const [editingResched, setEditingResched] = useState(false);
    const reschedSnapshotRef = useRef(reschedForm);

    // ── Visit Notes edit policy ───────────────────────────────────────────────
    // true = any_with_access (any credentialed doctor may edit any entry),
    // false = primary_only (the safe default).
    const [anyDoctorCanEdit, setAnyDoctorCanEdit] = useState(false);
    const [savingVisitNotePolicy, setSavingVisitNotePolicy] = useState(false);
    const [editingVisitNotePolicy, setEditingVisitNotePolicy] = useState(false);
    const visitNotePolicySnapshotRef = useRef(anyDoctorCanEdit);

    // ── Doctor Notes edit policy ──────────────────────────────────────────────
    const [notePolicy, setNotePolicy] = useState<NoteEditPolicy>("primary_doctor_only");
    const [savingNotePolicy, setSavingNotePolicy] = useState(false);
    const [editingNotePolicy, setEditingNotePolicy] = useState(false);
    const notePolicySnapshotRef = useRef(notePolicy);

    // ── Immunization (IAP overlay) settings ───────────────────────────────────
    const [immunizationForm, setImmunizationForm] = useState({ iap_overlay_enabled: false });
    const [savingImmunization, setSavingImmunization] = useState(false);

    useEffect(() => {
        // Clinic Profile still rides the getClinicSettings stub (PUT/GET /clinic/settings
        // not built yet). Appointment config + HRF settings load from their real
        // endpoints. allSettled so one failing call doesn't blank the others.
        Promise.allSettled([
            SettingsAPI.getClinicSettings(),
            SettingsAPI.getAppointmentConfig(),
            SettingsAPI.getHRFSettings(),
            SettingsAPI.getReschedulePolicy(),
            SettingsAPI.getVisitNoteEditPolicy(),
            NotesAPI.getEditPolicy(),
            SettingsAPI.getImmunizationSettings(),
        ])
            .then(([clinicRes, apptRes, hrfRes, reschedRes, visitNotePolicyRes, notePolicyRes, immunizationRes]) => {
                if (clinicRes.status === "fulfilled") {
                    const data = clinicRes.value;
                    const { daysOpen, opensAt, closesAt } = parseTimingsDisplay(data.timings_display);
                    const savedTimings = data.timings_display.trim();
                    setLegacyTimings(
                        savedTimings && buildTimingsDisplay(daysOpen, opensAt, closesAt) !== savedTimings
                            ? savedTimings
                            : null,
                    );
                    setClinicForm({
                        name:  data.name,
                        ...splitAddress(data.address),
                        phone: data.phone,
                        email: data.email,
                        website: data.website,
                        opensAt,
                        closesAt,
                        daysOpen,
                    });
                    setLogoUrl(data.logo_url ?? null);
                    setLetterheadUrl(data.letterhead_url ?? null);
                }
                if (apptRes.status === "fulfilled") {
                    setApptForm(apptRes.value);
                    savedSlotDurationRef.current = apptRes.value.slot_duration_minutes;
                    cacheAppointmentConfig(apptRes.value);   // keep the booking/roster cache warm
                }
                if (hrfRes.status === "fulfilled") {
                    setHrfForm(hrfRes.value);
                    cacheHrfSettings(hrfRes.value);   // keep the visit-flow cache warm
                }
                if (reschedRes.status === "fulfilled") {
                    setReschedForm(reschedRes.value);
                }
                if (visitNotePolicyRes.status === "fulfilled") {
                    setAnyDoctorCanEdit(visitNotePolicyRes.value.visit_note_edit_policy === "any_with_access");
                    cacheVisitNoteEditPolicy(visitNotePolicyRes.value);
                }
                if (notePolicyRes.status === "fulfilled") {
                    setNotePolicy(notePolicyRes.value.note_edit_policy);
                    cacheNoteEditPolicy(notePolicyRes.value.note_edit_policy);
                }
                if (immunizationRes.status === "fulfilled") {
                    setImmunizationForm(immunizationRes.value);
                }
                if (
                    apptRes.status === "rejected" || hrfRes.status === "rejected" ||
                    reschedRes.status === "rejected" || visitNotePolicyRes.status === "rejected" ||
                    notePolicyRes.status === "rejected" || immunizationRes.status === "rejected"
                ) {
                    toast.error("Some clinic settings failed to load");
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const handleEditClinic = () => {
        clinicSnapshotRef.current = clinicForm;
        setEditingClinic(true);
    };

    const handleCancelEditClinic = () => {
        setClinicForm(clinicSnapshotRef.current);
        setWebsiteError(undefined);
        setEditingClinic(false);
    };

    const handleSaveClinic = async () => {
        const error = validateWebsite(clinicForm.website);
        if (error) {
            setWebsiteError(error);
            return;
        }
        // Address and timings are rebuilt from the split fields, so only send them
        // when the user changed those fields. Otherwise a save made just to add a
        // website would rewrite the stored text (and the prescription header).
        const snapshot = clinicSnapshotRef.current;
        const address = joinAddress(clinicForm);
        const timings = buildTimingsDisplay(clinicForm.daysOpen, clinicForm.opensAt, clinicForm.closesAt);
        const timingsChanged =
            timings !== buildTimingsDisplay(snapshot.daysOpen, snapshot.opensAt, snapshot.closesAt);
        setSavingClinic(true);
        try {
            await SettingsAPI.saveClinicSettings({
                name:    clinicForm.name,
                phone:   clinicForm.phone,
                email:   clinicForm.email,
                website: clinicForm.website,
                ...(address !== joinAddress(snapshot) && { address }),
                ...(timingsChanged && { timings_display: timings }),
            });
            if (timingsChanged) setLegacyTimings(null);
            setEditingClinic(false);
            toast.success("Clinic profile saved");
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to save clinic profile"));
        } finally {
            setSavingClinic(false);
        }
    };

    const toggleDay = (idx: number) => {
        setClinicForm(p => ({
            ...p,
            daysOpen: p.daysOpen.includes(idx)
                ? p.daysOpen.filter(d => d !== idx)
                : [...p.daysOpen, idx].sort((a, b) => a - b),
        }));
    };

    const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";   // allow re-selecting the same file
        if (!file) return;
        if (!["image/jpeg", "image/png"].includes(file.type)) {
            toast.error("Invalid format. Only JPG and PNG images are supported.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image too large. Maximum file size is 5 MB.");
            return;
        }
        setUploadingLogo(true);
        try {
            const updated = await SettingsAPI.uploadClinicLogo(file);
            setLogoUrl(updated.logo_url ?? null);
            toast.success("Clinic logo updated");
        } catch (err: any) {
            const detail = err?.body?.detail || err?.detail;
            toast.error(typeof detail === "string" ? detail : "Failed to upload clinic logo. Please try a JPG or PNG image under 5 MB.");
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleDeleteLogo = async () => {
        setDeletingLogo(true);
        try {
            const updated = await SettingsAPI.deleteClinicLogo();
            setLogoUrl(updated.logo_url ?? null);
            toast.success("Clinic logo removed");
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to remove clinic logo"));
        } finally {
            setDeletingLogo(false);
        }
    };

    // Modal actions: "Upload" hands off to the hidden file input; "Delete"
    // removes the logo, then both close the modal.
    const handleModalUpload = () => {
        setShowLogoModal(false);
        logoInputRef.current?.click();
    };

    const handleModalDelete = async () => {
        await handleDeleteLogo();
        setShowLogoModal(false);
    };

    const handleLetterheadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!["image/jpeg", "image/png"].includes(file.type)) {
            toast.error("Please choose a JPG or PNG image");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Letterhead must be under 2 MB");
            return;
        }
        setUploadingLetterhead(true);
        try {
            const updated = await SettingsAPI.uploadClinicLetterhead(file);
            setLetterheadUrl(updated.letterhead_url ?? null);
            toast.success("Prescription letterhead updated");
        } catch (err) {
            // 403 -> name the missing permission; otherwise show the server's
            // reason (e.g. an unreadable image) rather than a generic failure.
            toast.error(
                err instanceof Error && !isPermissionDenied(err)
                    ? err.message
                    : actionErrorMessage(err, "Failed to upload letterhead"),
            );
        } finally {
            setUploadingLetterhead(false);
        }
    };

    const handleDeleteLetterhead = async () => {
        setDeletingLetterhead(true);
        try {
            const updated = await SettingsAPI.deleteClinicLetterhead();
            setLetterheadUrl(updated.letterhead_url ?? null);
            toast.success("Letterhead removed");
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to remove letterhead"));
        } finally {
            setDeletingLetterhead(false);
        }
    };

    const handleLetterheadModalUpload = () => {
        setShowLetterheadModal(false);
        letterheadInputRef.current?.click();
    };

    const handleLetterheadModalDelete = async () => {
        await handleDeleteLetterhead();
        setShowLetterheadModal(false);
    };

    const handleEditAppt = () => {
        apptSnapshotRef.current = apptForm;
        setEditingAppt(true);
    };

    const handleCancelEditAppt = () => {
        setApptForm(apptSnapshotRef.current);
        setEditingAppt(false);
    };

    const handleSaveAppt = async () => {
        const durationChanged = apptForm.slot_duration_minutes !== savedSlotDurationRef.current;
        setSavingAppt(true);
        try {
            await SettingsAPI.saveAppointmentConfig(apptForm);
            cacheAppointmentConfig(apptForm);   // booking + roster pick up the new defaults right away
            savedSlotDurationRef.current = apptForm.slot_duration_minutes;
            setEditingAppt(false);
            // A duration change cascades clinic-wide: every doctor's active days
            // adopt the new length and their upcoming free slots are rebuilt
            // around any already-booked slot.
            toast.success(
                durationChanged
                    ? `Slot duration set to ${apptForm.slot_duration_minutes} min — upcoming slots rebuilt for all doctors`
                    : "Appointment config saved"
            );
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to save appointment config"));
        } finally {
            setSavingAppt(false);
        }
    };

    const handleEditHrf = () => {
        hrfSnapshotRef.current = hrfForm;
        setEditingHrf(true);
    };

    const handleCancelEditHrf = () => {
        setHrfForm(hrfSnapshotRef.current);
        setEditingHrf(false);
    };

    const handleSaveHrf = async () => {
        setSavingHrf(true);
        try {
            await SettingsAPI.saveHRFSettings(hrfForm);
            cacheHrfSettings(hrfForm);   // visit flow enforces the new rules right away
            setEditingHrf(false);
            toast.success("HRF settings saved");
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to save HRF settings"));
        } finally {
            setSavingHrf(false);
        }
    };

    const handleEditResched = () => {
        reschedSnapshotRef.current = reschedForm;
        setEditingResched(true);
    };

    const handleCancelEditResched = () => {
        setReschedForm(reschedSnapshotRef.current);
        setEditingResched(false);
    };

    const handleSaveResched = async () => {
        setSavingResched(true);
        try {
            const saved = await SettingsAPI.saveReschedulePolicy(reschedForm);
            // Show what the server actually stored, not just what was typed.
            if (saved) setReschedForm(saved);
            setEditingResched(false);
            toast.success("Reschedule policy saved");
        } catch (e: any) {
            toast.error(e?.message || "Failed to save reschedule policy");
        } finally {
            setSavingResched(false);
        }
    };

    const handleEditVisitNotePolicy = () => {
        visitNotePolicySnapshotRef.current = anyDoctorCanEdit;
        setEditingVisitNotePolicy(true);
    };

    const handleCancelEditVisitNotePolicy = () => {
        setAnyDoctorCanEdit(visitNotePolicySnapshotRef.current);
        setEditingVisitNotePolicy(false);
    };

    const handleSaveVisitNotePolicy = async () => {
        setSavingVisitNotePolicy(true);
        try {
            const payload: VisitNoteEditPolicySettings = {
                visit_note_edit_policy: anyDoctorCanEdit ? "any_with_access" : "primary_only",
            };
            await SettingsAPI.saveVisitNoteEditPolicy(payload);
            cacheVisitNoteEditPolicy(payload);   // notes panel reflects the new policy right away
            setEditingVisitNotePolicy(false);
            toast.success("Visit Notes edit policy saved");
        } catch {
            toast.error("Failed to save Visit Notes edit policy");
        } finally {
            setSavingVisitNotePolicy(false);
        }
    };

    const handleEditNotePolicy = () => {
        notePolicySnapshotRef.current = notePolicy;
        setEditingNotePolicy(true);
    };

    const handleCancelEditNotePolicy = () => {
        setNotePolicy(notePolicySnapshotRef.current);
        setEditingNotePolicy(false);
    };

    const handleSaveNotePolicy = async () => {
        setSavingNotePolicy(true);
        try {
            const data = await NotesAPI.updateEditPolicy(notePolicy);
            cacheNoteEditPolicy(data.note_edit_policy);   // notes panel enforces the new policy right away
            setEditingNotePolicy(false);
            toast.success("Doctor Notes edit policy saved");
        } catch (err) {
            toast.error(actionErrorMessage(err, "Failed to save Doctor Notes edit policy"));
        } finally {
            setSavingNotePolicy(false);
        }
    };

    const handleToggleIapOverlay = async () => {
        const previous = immunizationForm;
        const next = { iap_overlay_enabled: !immunizationForm.iap_overlay_enabled };
        // Optimistic update - the backend regenerates every active patient's
        // calendar before responding, which can take a real, visible amount
        // of time on a clinic with many patients. Flip the toggle immediately
        // so it feels responsive, and roll back only if the save actually fails.
        setImmunizationForm(next);
        setSavingImmunization(true);
        try {
            await SettingsAPI.saveImmunizationSettings(next);
            toast.success(
                next.iap_overlay_enabled
                    ? "IAP overlay enabled — optional doses added to existing calendars"
                    : "IAP overlay disabled"
            );
        } catch {
            setImmunizationForm(previous);
            toast.error("Failed to save immunization settings");
        } finally {
            setSavingImmunization(false);
        }
    };

    if (loading) {
        return <div className="text-sm text-gray-400 py-10 text-center">Loading…</div>;
    }

    const inputCls =
        "w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

    const selectCls =
        "w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer";

    // Locked appearance when a card is not in edit mode.
    const lockedCls =
        "w-full border border-gray-100 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-700 bg-gray-50 cursor-not-allowed";
    const lockedSelectCls = lockedCls + " appearance-none";

    const clinicFieldCls = editingClinic ? inputCls : lockedCls;

    return (
        <div className="space-y-5">

            {/* ── Card 0: Organisation (owner only) ─────────────────────────
                Hidden for everyone else: a single-clinic admin has no
                organisation, and non-owner staff must not rename one. */}
            {isOrgOwner && (
                <SettingsCard
                    title="Organisation"
                    icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 21h18M5 21V7l7-4 7 4v14"/>
                            <path d="M9 21v-6h6v6"/>
                        </svg>
                    }
                    footer={
                        <EditableCardFooter
                            editing={editingOrg}
                            saving={savingOrg}
                            onEdit={handleEditOrg}
                            onSave={handleSaveOrg}
                            onCancel={handleCancelEditOrg}
                        />
                    }
                >
                    <div className="max-w-md">
                        <label className="block text-sm font-medium text-slate-700">
                            Organisation name
                        </label>
                        <input
                            type="text"
                            value={orgName}
                            maxLength={255}
                            disabled={!editingOrg || savingOrg}
                            onChange={(e) => setOrgName(e.target.value)}
                            placeholder="Mehta Health Group"
                            className="mt-1.5 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500"
                        />
                        <p className="mt-2 text-xs leading-relaxed text-slate-500">
                            The group your clinics belong to. Shown above the clinic
                            switcher and on the clinic picker at sign-in. Renaming it
                            does not change any clinic&apos;s own name.
                        </p>
                    </div>
                </SettingsCard>
            )}

            {/* ── Card 1: Clinic Profile ────────────────────────────────── */}
            <SettingsCard
                title="Clinic Profile"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                        <polyline points="9 22 9 12 15 12 15 22"/>
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingClinic}
                        saving={savingClinic}
                        onEdit={handleEditClinic}
                        onSave={handleSaveClinic}
                        onCancel={handleCancelEditClinic}
                    />
                }
            >
                <div className="flex items-start gap-5">

                    {/* Clinic logo — upload JPG/PNG */}
                    <div className="relative flex-shrink-0">
                        <div className="w-24 h-24 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center overflow-hidden">
                            {logoUrl ? (
                                <img src={logoUrl} alt="Clinic logo" className="w-full h-full object-cover" />
                            ) : (
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                                    stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                                    <polyline points="9 22 9 12 15 12 15 22"/>
                                </svg>
                            )}
                        </div>
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            onChange={handleLogoChange}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => setShowLogoModal(true)}
                            disabled={uploadingLogo || deletingLogo}
                            className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-sm hover:bg-blue-600 disabled:opacity-50"
                            title="Change clinic logo"
                        >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                            </svg>
                        </button>
                    </div>

                    {/* Fields */}
                    <div className="flex-1 space-y-4 min-w-0">

                        {/* Clinic Name */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Clinic Name</label>
                            <input className={clinicFieldCls} value={clinicForm.name}
                                onChange={e => setClinicForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="HealthCare Plus Clinic"
                                disabled={!editingClinic} />
                        </div>

                        {/* Street Address */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Street Address</label>
                            <input className={clinicFieldCls} value={clinicForm.streetAddress}
                                onChange={e => setClinicForm(p => ({ ...p, streetAddress: e.target.value }))}
                                placeholder="123 MG Road"
                                disabled={!editingClinic} />
                        </div>

                        {/* City / Pincode / State — same split as onboarding */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
                                <input className={clinicFieldCls} value={clinicForm.city}
                                    onChange={e => setClinicForm(p => ({ ...p, city: e.target.value }))}
                                    placeholder="Bangalore"
                                    disabled={!editingClinic} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Pincode</label>
                                <input className={clinicFieldCls} value={clinicForm.pincode}
                                    onChange={e => setClinicForm(p => ({ ...p, pincode: e.target.value }))}
                                    placeholder="560001"
                                    disabled={!editingClinic} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
                                <input className={clinicFieldCls} value={clinicForm.state}
                                    onChange={e => setClinicForm(p => ({ ...p, state: e.target.value }))}
                                    placeholder="Karnataka"
                                    disabled={!editingClinic} />
                            </div>
                        </div>

                        {/* Contact + Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Contact Number</label>
                                <input className={clinicFieldCls} value={clinicForm.phone}
                                    onChange={e => setClinicForm(p => ({ ...p, phone: e.target.value }))}
                                    placeholder="+91 80 1234 5678"
                                    disabled={!editingClinic} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                                <input className={clinicFieldCls} value={clinicForm.email}
                                    onChange={e => setClinicForm(p => ({ ...p, email: e.target.value }))}
                                    placeholder="contact@healthcareplus.com"
                                    type="email"
                                    disabled={!editingClinic} />
                            </div>
                        </div>

                        {/* Website */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Website</label>
                            <input className={websiteError ? clinicFieldCls.replace("border-gray-200", "border-red-300") : clinicFieldCls}
                                value={clinicForm.website}
                                onChange={e => {
                                    setClinicForm(p => ({ ...p, website: e.target.value }));
                                    if (websiteError) setWebsiteError(undefined);
                                }}
                                placeholder="https://healthcareplus.com"
                                disabled={!editingClinic} />
                            {websiteError && <p className="mt-1 text-xs text-red-600">{websiteError}</p>}
                        </div>

                        {/* Operating Hours */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Opens At</label>
                                <input className={clinicFieldCls} type="time" value={clinicForm.opensAt}
                                    onChange={e => setClinicForm(p => ({ ...p, opensAt: e.target.value }))}
                                    disabled={!editingClinic} />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Closes At</label>
                                <input className={clinicFieldCls} type="time" value={clinicForm.closesAt}
                                    onChange={e => setClinicForm(p => ({ ...p, closesAt: e.target.value }))}
                                    disabled={!editingClinic} />
                            </div>
                        </div>
                        {legacyTimings && (
                            <p className="-mt-2 text-xs text-gray-500">
                                Currently saved as “{legacyTimings}”. This is kept as is unless you change the hours or days below.
                            </p>
                        )}

                        {/* Days Open */}
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Days Open</label>
                            <div className="flex flex-wrap gap-2">
                                {DAY_LABELS.map((label, idx) => {
                                    const selected = clinicForm.daysOpen.includes(idx);
                                    return (
                                        <button
                                            key={label}
                                            type="button"
                                            disabled={!editingClinic}
                                            onClick={() => toggleDay(idx)}
                                            className={
                                                selected
                                                    ? "px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                                                    : "px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 bg-white disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                                            }
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                    </div>
                </div>
            </SettingsCard>

            {/* Staff & Roles is managed in Settings → Access & Personalization
                (Access Control & Permissions → Add Member). */}

            {/* Clinic logo — upload / delete */}
            <ImageActionsModal
                isOpen={showLogoModal}
                onClose={() => setShowLogoModal(false)}
                title="Clinic Logo"
                subtitle="Upload a new logo or remove the current one"
                noun="logo"
                hasImage={!!logoUrl}
                uploading={uploadingLogo}
                deleting={deletingLogo}
                onUploadClick={handleModalUpload}
                onDelete={handleModalDelete}
            />

            {/* ── Card 2b: Prescription Letterhead ─────────────────────── */}
            <SettingsCard
                title="Prescription Letterhead"
                subtitle="Replaces the default header on your prescription PDFs"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <path d="M3 9h18"/>
                        <path d="M9 21V9"/>
                    </svg>
                }
            >
                <div className="flex items-start gap-5">
                    {/* Letterhead preview */}
                    <div className="relative flex-shrink-0">
                        <div className="w-40 h-14 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center overflow-hidden">
                            {letterheadUrl ? (
                                <img
                                    src={letterheadUrl}
                                    alt="Prescription letterhead"
                                    className="w-full h-full object-contain"
                                />
                            ) : (
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
                                    stroke="#93c5fd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                                    <path d="M3 9h18"/><path d="M9 21V9"/>
                                </svg>
                            )}
                        </div>
                        <input
                            ref={letterheadInputRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            onChange={handleLetterheadChange}
                            className="hidden"
                        />
                        <button
                            type="button"
                            onClick={() => setShowLetterheadModal(true)}
                            disabled={uploadingLetterhead || deletingLetterhead}
                            className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white shadow-sm hover:bg-blue-600 disabled:opacity-50"
                            title="Change prescription letterhead"
                        >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                            </svg>
                        </button>
                    </div>

                    {/* Description */}
                    <div className="flex-1 min-w-0 pt-1 space-y-2">
                        <p className="text-sm text-gray-700">
                            {letterheadUrl
                                ? "Your custom letterhead will appear in the header of every new prescription PDF."
                                : "No letterhead uploaded — prescriptions use the default clinic name header."}
                        </p>
                        <p className="text-xs text-gray-400">
                            PNG or JPG, under 2 MB — any size or shape works, we'll fit it in automatically.
                        </p>
                        {(uploadingLetterhead || deletingLetterhead) && (
                            <p className="text-xs text-blue-500">
                                {uploadingLetterhead ? "Uploading…" : "Removing…"}
                            </p>
                        )}
                    </div>
                </div>
            </SettingsCard>

            {/* Letterhead — upload / delete modal */}
            <ImageActionsModal
                isOpen={showLetterheadModal}
                onClose={() => setShowLetterheadModal(false)}
                title="Prescription Letterhead"
                subtitle="Upload a new letterhead or remove the current one"
                noun="letterhead"
                uploadHint="PNG or JPG, 2480 × 600 px recommended, under 2 MB"
                hasImage={!!letterheadUrl}
                uploading={uploadingLetterhead}
                deleting={deletingLetterhead}
                onUploadClick={handleLetterheadModalUpload}
                onDelete={handleLetterheadModalDelete}
            />

            {/* ── Card 3: Appointment Configuration ────────────────────── */}
            <SettingsCard
                title="Appointment Configuration"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8"  y1="2" x2="8"  y2="6"/>
                        <line x1="3"  y1="10" x2="21" y2="10"/>
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingAppt}
                        saving={savingAppt}
                        onEdit={handleEditAppt}
                        onSave={handleSaveAppt}
                        onCancel={handleCancelEditAppt}
                    />
                }
            >
                <div className="space-y-4">
                    {/* Slot duration + Max bookings */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Default Slot Duration</label>
                            <div className="relative">
                                <select
                                    className={editingAppt ? selectCls : lockedSelectCls}
                                    value={apptForm.slot_duration_minutes}
                                    onChange={e => setApptForm(p => ({ ...p, slot_duration_minutes: Number(e.target.value) }))}
                                    disabled={!editingAppt}
                                >
                                    <option value={10}>10 minutes</option>
                                    <option value={15}>15 minutes</option>
                                    <option value={20}>20 minutes</option>
                                    <option value={30}>30 minutes</option>
                                </select>
                                <svg className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                                    width="13" height="13" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="6 9 12 15 18 9"/>
                                </svg>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Max Daily Bookings</label>
                            <input
                                className={editingAppt ? inputCls : lockedCls}
                                type="number"
                                min={1}
                                value={apptForm.max_daily_bookings}
                                onChange={e => setApptForm(p => ({ ...p, max_daily_bookings: Number(e.target.value) }))}
                                placeholder="50"
                                disabled={!editingAppt}
                            />
                        </div>
                    </div>

                    {/* Allow online booking */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div>
                            <p className="text-sm font-medium text-gray-900">Allow Online Booking</p>
                            <p className="text-xs text-gray-500 mt-0.5">Enable patients to book appointments online</p>
                        </div>
                        <Toggle
                            enabled={apptForm.allow_online_booking}
                            onToggle={() => setApptForm(p => ({ ...p, allow_online_booking: !p.allow_online_booking }))}
                            disabled={!editingAppt}
                        />
                    </div>
                </div>
            </SettingsCard>

            {/* ── Card 4: Visit Settings (HRF & MPR) ───────────────────── */}
            <SettingsCard
                title="Visit Settings"
                subtitle="Health Record Format configuration"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingHrf}
                        saving={savingHrf}
                        onEdit={handleEditHrf}
                        onSave={handleSaveHrf}
                        onCancel={handleCancelEditHrf}
                    />
                }
            >
                <div>
                    {([
                        { key: "mandatory_vitals",    label: "Mandatory Vitals Recording",  sub: "Require vitals for every patient"      },
                        { key: "mandatory_diagnosis", label: "Mandatory Diagnosis Entry",    sub: "Diagnosis must be documented"          },
                        { key: "lab_report_uploads",  label: "Lab Report Uploads",           sub: "Require lab reports to be attached"    },
                    ] as const).map(({ key, label, sub }, i, arr) => (
                        <div key={key}
                            className={`flex items-center justify-between py-4 ${i < arr.length - 1 ? "border-b border-gray-100" : ""}`}>
                            <div>
                                <p className="text-sm font-medium text-gray-900">{label}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
                            </div>
                            <Toggle
                                enabled={hrfForm[key]}
                                onToggle={() => setHrfForm(p => ({ ...p, [key]: !p[key] }))}
                                disabled={!editingHrf}
                            />
                        </div>
                    ))}
                </div>
            </SettingsCard>

            {/* ── Card 5: Reschedule Policy ─────────────────────────────── */}
            <SettingsCard
                title="Reschedule Policy"
                subtitle="Guardrails for the patient self-service reschedule link"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="1 4 1 10 7 10"/>
                        <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingResched}
                        saving={savingResched}
                        onEdit={handleEditResched}
                        onSave={handleSaveResched}
                        onCancel={handleCancelEditResched}
                    />
                }
            >
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Self-Reschedule Cutoff (hours)</label>
                        <input
                            className={editingResched ? inputCls : lockedCls}
                            type="number"
                            min={0}
                            value={reschedForm.self_reschedule_cutoff_hours}
                            onChange={e => setReschedForm(p => ({ ...p, self_reschedule_cutoff_hours: Number(e.target.value) }))}
                            disabled={!editingResched}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Max Self-Reschedules per Appointment</label>
                        <input
                            className={editingResched ? inputCls : lockedCls}
                            type="number"
                            min={0}
                            value={reschedForm.max_self_reschedules_per_patient}
                            onChange={e => setReschedForm(p => ({ ...p, max_self_reschedules_per_patient: Number(e.target.value) }))}
                            disabled={!editingResched}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Excessive Churn Threshold</label>
                        <input
                            className={editingResched ? inputCls : lockedCls}
                            type="number"
                            min={1}
                            value={reschedForm.excessive_churn_threshold}
                            onChange={e => setReschedForm(p => ({ ...p, excessive_churn_threshold: Number(e.target.value) }))}
                            disabled={!editingResched}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Churn Window (days)</label>
                        <input
                            className={editingResched ? inputCls : lockedCls}
                            type="number"
                            min={1}
                            value={reschedForm.excessive_churn_window_days}
                            onChange={e => setReschedForm(p => ({ ...p, excessive_churn_window_days: Number(e.target.value) }))}
                            disabled={!editingResched}
                        />
                    </div>
                </div>
            </SettingsCard>

            {/* ── Card 6: Visit Notes Edit Policy ──────────────────────── */}
            <SettingsCard
                title="Visit Notes Edit Policy"
                subtitle="Who can edit another doctor's visit note entries"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingVisitNotePolicy}
                        saving={savingVisitNotePolicy}
                        onEdit={handleEditVisitNotePolicy}
                        onSave={handleSaveVisitNotePolicy}
                        onCancel={handleCancelEditVisitNotePolicy}
                    />
                }
            >
                <div className="flex items-center justify-between py-4">
                    <div>
                        <p className="text-sm font-medium text-gray-900">Any doctor can edit visit notes</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {anyDoctorCanEdit
                                ? "Any credentialed doctor in the clinic can edit any doctor's note entries — for tightly-integrated group practices."
                                : "Only the visit's primary doctor or a note's own author can edit it — the safe default."}
                        </p>
                    </div>
                    <Toggle
                        enabled={anyDoctorCanEdit}
                        onToggle={() => setAnyDoctorCanEdit(p => !p)}
                        disabled={!editingVisitNotePolicy}
                    />
                </div>
            </SettingsCard>

            {/* ── Card 7: Doctor Notes ─────────────────────────────────── */}
            <SettingsCard
                title="Doctor Notes"
                subtitle="Who may edit an existing note entry"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                }
                footer={
                    <EditableCardFooter
                        editing={editingNotePolicy}
                        saving={savingNotePolicy}
                        onEdit={handleEditNotePolicy}
                        onSave={handleSaveNotePolicy}
                        onCancel={handleCancelEditNotePolicy}
                    />
                }
            >
                <div className="flex items-center justify-between py-4">
                    <div>
                        <p className="text-sm font-medium text-gray-900">Any doctor can edit notes</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Lets any doctor edit any doctor's note — for tightly integrated group practices.
                            Off (default): only the visit's primary doctor, or the original author, may edit.
                        </p>
                    </div>
                    <Toggle
                        enabled={notePolicy === "any_clinic_doctor"}
                        onToggle={() =>
                            setNotePolicy(p => (p === "any_clinic_doctor" ? "primary_doctor_only" : "any_clinic_doctor"))
                        }
                        disabled={!editingNotePolicy}
                    />
                </div>
            </SettingsCard>

            {/* ── Clinic Closure (mock: pending Phase 1 clinic-lifecycle data) ──
                Dev builds only: it tells every clinic it is closing. Same gate as
                ClinicClosureBanner on the dashboard. */}
            {process.env.NODE_ENV === "development" && <ClinicClosureCard />}

            {/* ── Card 6: Immunization Schedule ───────────────────────────── */}
            {/* Hidden while the "immunization" clinic flag is off - it defaults OFF
                for every clinic (hybrid included) until Sid's clinical sign-off on
                the NIS/IAP schedule lands. See useFeatureFlags' FEATURE_META entry. */}
            {immunizationEnabled && (
                <SettingsCard
                    title="Immunization Schedule"
                    subtitle="Pediatric vaccination calendar configuration"
                    icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                    }
                >
                    <div className="flex items-center justify-between py-2">
                        <div>
                            <p className="text-sm font-medium text-gray-900">IAP Overlay</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Add IAP-recommended optional doses on top of the always-on NIS base
                                calendar. Toggling this regenerates every active patient's calendar —
                                already-administered doses are never disturbed.
                            </p>
                        </div>
                        <Toggle
                            enabled={immunizationForm.iap_overlay_enabled}
                            onToggle={handleToggleIapOverlay}
                            disabled={savingImmunization}
                        />
                    </div>
                </SettingsCard>
            )}

        </div>
    );
};
